'use client';

import React, { useState, useEffect, useLayoutEffect } from 'react';
import Link from 'next/link';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Container, Group, Image } from '@mantine/core';
import { ProfileContextProvider } from '@/context/Profile';
import { useRouter, usePathname } from 'next/navigation';
import { ACTIONS, LIFECYCLE } from 'react-joyride';

import LogoImage from '../../public/img/logo.svg';
import LoginButton from './auth/LoginButton';
import { useCurrentUser } from '@/hooks/use-current-user';
import Button from './Button';
import { useAuthContext } from '@/context/Auth';
import { homePageProfileNavigationSteps } from '@/components/UserGuide/steps';
import UserGuide from './UserGuide/UserGuide';
import { getAuth } from 'firebase/auth';
import { userGuideService } from '@/services/userGuideService';
import FAIcon from './FAIcon';

// ✅ Use this hook to run code before paint (avoids flash)
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Types - Define locally to avoid server import issues
 */
interface MenuItem {
  id: string;
  menuGroup: string;
  parentId: string | null;
  type: string;
  title: string;
  pageId: string | null;
  postId: string | null;
  slug: string;
  url: string;
  routeName: string | null;
  routeParams: string | null;
  icon: string | null;
  color: string | null;
  target: string;
  isActive: boolean;
  isPublished: boolean;
  meta: any;
  created_at: string;
  updated_at: string;
  order: number;
  children?: MenuItem[];
}

interface TopbarSettings {
  enabled: boolean;
  backgroundColor: string;
  textColor: string;
  message: string;
  linkText: string;
  linkUrl: string;
  linkTarget: '_self' | '_blank';
  linkType?: 'external' | 'internal' | 'section';
  pageId?: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  position: 'top' | 'bottom';
  dismissible: boolean;
  showOnMobile: boolean;
}

interface LogoSettings {
  imageUrl: string | null;
  altText: string;
}

interface CMSSettings {
  topbar: TopbarSettings;
  logo: LogoSettings;
}

/**
 * Topbar Styled Component
 */
const TopbarWrapper = styled.div<{
  isVisible: boolean;
  backgroundColor: string;
  textColor: string;
  showOnMobile: boolean;
  disableTransition?: boolean;
}>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 200;
  background-color: ${({ backgroundColor }) => backgroundColor};
  color: ${({ textColor }) => textColor};
  transform: translateY(${({ isVisible }) => (isVisible ? '0' : '-100%')});
  transition: ${({ disableTransition }) => disableTransition ? 'none' : 'transform 300ms ease-in-out'};

  @media screen and (max-width: 768px) {
    display: ${({ showOnMobile }) => (showOnMobile ? 'block' : 'none')};
  }
`;

const TopbarContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 8px 60px 8px 16px;

  @media screen and (max-width: 768px) {
    padding: 8px 50px 8px 12px;
    min-height: 40px;
  }
`;

const TopbarMessage = styled.span`
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  margin-right: 10px;

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
`;

const TopbarRightSection = styled.div`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 8px;

  @media screen and (max-width: 768px) {
    right: 8px;
    gap: 6px;
  }
`;

const TopbarButton = styled.a<{
  buttonTextColor: string;
  buttonBackgroundColor: string;
}>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ buttonTextColor }) => buttonTextColor};
  border: 1px solid ${({ buttonBackgroundColor, buttonTextColor }) =>
    buttonBackgroundColor === 'transparent' ? buttonTextColor : buttonBackgroundColor};
  border-radius: 20px;
  text-decoration: none;
  background: ${({ buttonBackgroundColor }) =>
    buttonBackgroundColor === 'transparent' ? 'transparent' : buttonBackgroundColor};
  cursor: pointer;
  transition: background-color 200ms ease, transform 200ms ease, opacity 200ms ease;

  &:hover {
    opacity: 0.85;
    transform: scale(1.02);
  }

  @media screen and (max-width: 768px) {
    padding: 3px 8px;
    font-size: 10px;
  }
`;

const TopbarCloseButton = styled.button<{ textColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: none;
  color: ${({ textColor }) => textColor};
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 200ms ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  @media screen and (max-width: 768px) {
    width: 20px;
    height: 20px;
  }
`;

/**
 * SiteHeader Root Component
 */
const Root = styled('header', {
  shouldForwardProp: (prop: PropertyKey) =>
    !['scheme', 'mobileMenuOpen', 'topbarHeight', 'disableTransition'].includes(prop.toString()),
})<{ scheme?: 'light' | 'dark'; mobileMenuOpen: boolean; topbarHeight: number; disableTransition?: boolean }>(
  ({ scheme = 'dark', mobileMenuOpen = false, topbarHeight = 0, disableTransition = false }) => css`
    position: fixed;
    top: ${topbarHeight}px;
    left: 0;
    width: 100%;
    height: 80px;
    z-index: 199;
    display: flex;
    align-items: center;
    color: ${{
      light: '#fff',
      dark: '#222',
    }[scheme]};
    transition: ${disableTransition ? 'none' : '150ms height ease-in-out, 150ms color ease-in-out, 150ms background-color ease-in-out, 150ms box-shadow ease-in-out, 300ms top ease-in-out'};

    > div {
      display: flex;
      align-items: center;
      flex: 1;
    }

    #site-logo {
      width: 200px;
      transform-origin: left center;
      transition: 150ms transform ease-in-out, 200ms color ease-in-out;

      svg {
        display: block;
        color: inherit;
      }

      img {
        max-height: 45px;
      }

      @media screen and (max-width: 992px) {
        color: ${mobileMenuOpen ? '#333' : 'inherit'};
      }
    }

    .visually-hidden {
      position: absolute !important;
      height: 1px;
      width: 1px;
      overflow: hidden;
      clip: rect(1px, 1px, 1px, 1px);
      white-space: nowrap;
    }

    #site-nav {
      display: flex;
      align-items: center;
      margin-left: 2rem;

      @media screen and (max-width: 992px) {
        position: absolute;
        top: 0;
        left: 0;
        z-index: -1;
        width: 100%;
        height: 100vh;
        margin: 0;
        padding: 2rem;
        padding-bottom: 10rem;
        flex-direction: column;
        justify-content: center;
        font-size: 2rem;
        font-weight: 700;
        color: #222;
        background: var(--color-primary);
        transform: translateX(${mobileMenuOpen ? 0 : '100%'});
        transition: 220ms transform ease-in-out;
      }
    }

    #site-secondary-nav {
      @media screen and (max-width: 992px) {
        position: fixed;
        bottom: 20px;
        left: 0;
        width: 100%;
        justify-content: center;
        color: #222;
        transform: translateX(${mobileMenuOpen ? 0 : '100%'});
        transition: 220ms transform ease-in-out;
      }
    }

    #mobile-menu-toggle {
      display: none;

      @media screen and (max-width: 992px) {
        position: relative;
        display: block;
        margin-left: auto;
        width: 3rem;
        height: 3rem;
        font-size: 0;
        color: transparent;
        border: none;
        background: none;
        transition: 250ms transform ease-in-out;
        transform: ${mobileMenuOpen ? 'rotate(-180deg)' : 'rotate(0)'};

        &::before,
        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 2rem;
          height: 0.25rem;
          background-color: ${mobileMenuOpen
            ? '#333'
            : {
                light: '#fff',
                dark: '#222',
              }[scheme]};
          transition: 250ms transform ease-in-out;
        }

        &::before {
          transform: ${mobileMenuOpen
            ? 'translate(-50%, -50%) rotate(45deg)'
            : 'translate(-50%, calc(-50% - 0.5rem))'};
        }

        &::after {
          transform: ${mobileMenuOpen
            ? 'translate(-50%, -50%) rotate(-45deg)'
            : 'translate(-50%, calc(-50% + 0.5rem))'};
        }

        > span {
          display: block;
          width: 2rem;
          height: 0.25rem;
          margin: 0 auto;
          background-color: ${mobileMenuOpen
            ? '#333'
            : {
                light: '#fff',
                dark: '#222',
              }[scheme]};
          opacity: ${mobileMenuOpen ? 0 : 1};
          transition: 250ms opacity ease-in-out;
        }
      }
    }

    body.scrolled & {
      top: 0;
      height: 70px;
      color: #222;
      background-color: #ffffff;
      box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.15);
      transition-delay: 0ms, 0ms, 150ms, 0ms, 0ms;

      #site-logo {
        transform: scale(0.85);

        img {
          max-height: 40px;
        }
      }

      #mobile-menu-toggle {
        &::before,
        &::after {
          background-color: #333;
        }

        > span {
          background-color: #333;
        }
      }
    }
  `
);

/**
 * NavMenuItem Styled Components
 */
const NavMenuItemWrapper = styled.div<{ isActive?: boolean }>`
  position: relative;
  z-index: 0;

  > button {
    display: inline-flex;
    align-items: center;
    position: relative;
    padding: 0.75rem 1rem;
    background: none;
    border: none;
    color: inherit;
    font: inherit;
    font-weight: ${({ isActive }) => (isActive ? '600' : '400')};
    cursor: pointer;

    > span.underline-effect {
      position: absolute;
      top: 70%;
      left: 50%;
      width: 50%;
      height: 3px;
      z-index: -1;
      opacity: ${({ isActive }) => (isActive ? '1' : '0')};
      text-align: center;
      transform: translate(-50%, -50%);
      transition: 300ms transform cubic-bezier(0.27, 0.34, 0.56, 2.1) 50ms, 140ms opacity 50ms;

      > span {
        display: block;
        height: 100%;
        background-color: var(--color-primary);
        border-radius: 999px;
        transform: ${({ isActive }) => (isActive ? 'scaleX(1)' : 'scaleX(0)')};
        transform-origin: ${({ isActive }) => (isActive ? '0% center' : '100% center')};
        transition: 140ms transform ease-in-out;
      }
    }

    &:hover {
      > span.underline-effect {
        opacity: 1;
        transition: 100ms transform, 140ms opacity;

        > span {
          transform-origin: 0% center;
          transform: scaleX(1);
        }
      }
    }
  }

  @media screen and (max-width: 992px) {
    width: 100%;
    text-align: center;

    > button {
      width: 100%;
      justify-content: center;
      font-size: 1.5rem;
      font-weight: ${({ isActive }) => (isActive ? '700' : '600')};
    }
  }
`;

const NavMenuWrapper = styled.div`
  position: relative;
  display: inline-block;

  @media screen and (max-width: 992px) {
    width: 100%;
    text-align: center;
  }
`;

const NavMenuTrigger = styled.button<{
  hasChildren: boolean;
  isOpen?: boolean;
  isActive?: boolean;
}>`
  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  font: inherit;
  font-weight: ${({ isActive }) => (isActive ? '600' : '400')};
  padding: 0.75rem 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: opacity 200ms ease;

  &:hover {
    opacity: 0.8;
  }

  ${({ hasChildren, isOpen }) =>
    hasChildren &&
    css`
      &::after {
        content: '';
        display: inline-block;
        width: 6px;
        height: 6px;
        border-right: 2px solid currentColor;
        border-bottom: 2px solid currentColor;
        transform: rotate(${isOpen ? '-135deg' : '45deg'}) translateY(${isOpen ? '2px' : '-2px'});
        transition: transform 250ms ease;
        margin-left: 2px;
      }
    `}

  @media screen and (max-width: 992px) {
    width: 100%;
    justify-content: center;
    padding: 0.75rem 0;
    font-size: 1.5rem;
    font-weight: ${({ isActive }) => (isActive ? '700' : '600')};
  }
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 220px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 0.5rem;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transform: translateX(-50%) translateY(${({ isOpen }) => (isOpen ? '0' : '-10px')});
  transition: opacity 250ms ease, transform 250ms ease, visibility 250ms ease;
  z-index: 100;

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 12px;
    height: 12px;
    background: #ffffff;
    border-radius: 2px 0 0 0;
    box-shadow: -2px -2px 4px rgba(0, 0, 0, 0.04);
  }

  @media screen and (max-width: 992px) {
    position: static;
    box-shadow: none;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    min-width: auto;
    width: 90%;
    max-width: 300px;
    margin: 0.5rem auto;
    padding: ${({ isOpen }) => (isOpen ? '0.5rem 0' : '0')};
    max-height: ${({ isOpen }) => (isOpen ? '500px' : '0')};
    overflow: hidden;
    opacity: 1;
    visibility: visible;
    transform: none;
    transition: max-height 300ms ease, padding 300ms ease;

    &::before {
      display: none;
    }
  }
`;

const DropdownItem = styled.div`
  button {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.75rem 1rem;
    color: #333;
    text-decoration: none;
    font-size: 0.925rem;
    font-weight: 500;
    text-align: left;
    background: none;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 180ms ease, color 180ms ease, transform 180ms ease;

    &:hover {
      background-color: #f0f7ff;
      color: var(--color-primary, #007bff);
      transform: translateX(4px);
    }

    &:active {
      transform: translateX(2px);
    }
  }

  @media screen and (max-width: 992px) {
    button {
      justify-content: center;
      text-align: center;
      font-size: 1.25rem;
      padding: 0.75rem 1rem;
      color: #222;
      border-radius: 0;

      &:hover {
        background-color: rgba(255, 255, 255, 0.15);
        color: #222;
        transform: none;
      }
    }
  }
`;

/**
 * Props for SiteHeaderClient
 */
export interface SiteHeaderClientProps {
  scheme?: 'light' | 'dark';
  initialMenuList: MenuItem[];
  initialCmsSettings: CMSSettings;
}

/**
 * Wrapper component for LoginButton
 */
interface LoginButtonWrapperProps {
  profileMenuOpen: boolean;
  setProfileMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isTourActive: boolean;
}

const LoginButtonWrapper: React.FC<LoginButtonWrapperProps> = ({
  profileMenuOpen,
  setProfileMenuOpen,
  isTourActive,
}) => {
  return (
    <LoginButton
      profileMenuOpen={profileMenuOpen}
      setProfileMenuOpen={setProfileMenuOpen}
      isTourActive={isTourActive}
    />
  );
};

/**
 * SiteHeaderClient - Client Component
 * Handles all interactivity (mobile menu, dropdowns, scroll, etc.)
 */

// ✅ Default topbar height to prevent layout jump (matches min-height: 44px + padding)
const DEFAULT_TOPBAR_HEIGHT = 44;

const SiteHeaderClient: React.FC<SiteHeaderClientProps> = ({
  scheme = 'dark',
  initialMenuList,
  initialCmsSettings,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentUser] = useCurrentUser();
  const [mounted, setMounted] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // ✅ Track if we've checked sessionStorage yet
  const [hasCheckedStorage, setHasCheckedStorage] = useState(false);
  const [topbarDismissed, setTopbarDismissed] = useState(false);
  
  // ✅ Track if this is the initial hydration check (to skip animation)
  const [isInitialCheck, setIsInitialCheck] = useState(true);
  
  // ✅ Initialize with 0 - we'll set the correct value immediately on mount
  const [localTopbarHeight, setLocalTopbarHeight] = useState(0);
  const topbarRef = React.useRef<HTMLDivElement>(null);

  // Use server-provided data
  const cmsSettings = initialCmsSettings;
  const nestedMenuList = initialMenuList;

  // ✅ Check sessionStorage BEFORE first paint using useLayoutEffect
  useIsomorphicLayoutEffect(() => {
    // Check sessionStorage immediately (synchronously before paint)
    if (typeof window !== 'undefined') {
      const dismissed = sessionStorage.getItem('topbarDismissed') === 'true';
      setTopbarDismissed(dismissed);
      setHasCheckedStorage(true);
      
      // Set initial height AND CSS variables based on dismissed state
      if (!dismissed && cmsSettings?.topbar?.enabled && cmsSettings?.topbar?.message) {
        // Topbar should show - use default height until measured
        setLocalTopbarHeight(DEFAULT_TOPBAR_HEIGHT);
        document.documentElement.style.setProperty('--topbar-height', `${DEFAULT_TOPBAR_HEIGHT}px`);
        document.documentElement.style.setProperty('--header-height', '80px');
        document.documentElement.style.setProperty('--total-header-height', `${DEFAULT_TOPBAR_HEIGHT + 80}px`);
      } else {
        // Topbar dismissed or disabled - NO extra space
        setLocalTopbarHeight(0);
        document.documentElement.style.setProperty('--topbar-height', '0px');
        document.documentElement.style.setProperty('--header-height', '80px');
        document.documentElement.style.setProperty('--total-header-height', '80px');
      }
    }
  }, [cmsSettings?.topbar?.enabled, cmsSettings?.topbar?.message]);

  // ✅ Enable transitions after initial render
  useEffect(() => {
    // Small delay to ensure first paint has happened
    const timer = setTimeout(() => {
      setIsInitialCheck(false);
      setMounted(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // ✅ Update topbar height after render (for accurate measurement)
  useEffect(() => {
    if (!hasCheckedStorage) return;
    
    const shouldShowTopbar = cmsSettings?.topbar?.enabled && 
                             cmsSettings?.topbar?.message && 
                             !topbarDismissed && 
                             !isScrolled;
    
    if (topbarRef.current && shouldShowTopbar) {
      const height = topbarRef.current.offsetHeight;
      setLocalTopbarHeight(height);
      document.documentElement.style.setProperty('--topbar-height', `${height}px`);
      document.documentElement.style.setProperty('--total-header-height', `${height + 80}px`);
    } else if (!shouldShowTopbar) {
      setLocalTopbarHeight(0);
      document.documentElement.style.setProperty('--topbar-height', '0px');
      document.documentElement.style.setProperty('--header-height', isScrolled ? '70px' : '80px');
      document.documentElement.style.setProperty('--total-header-height', isScrolled ? '70px' : '80px');
    }
  }, [cmsSettings?.topbar?.enabled, cmsSettings?.topbar?.message, topbarDismissed, isScrolled, hasCheckedStorage]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setOpenDropdownId(null);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 0;
      setIsScrolled(scrolled);

      if (scrolled) {
        document.body.classList.add('scrolled');
        document.documentElement.style.setProperty('--header-height', '70px');
        document.documentElement.style.setProperty('--total-header-height', '70px');
      } else {
        document.body.classList.remove('scrolled');
        document.documentElement.style.setProperty('--header-height', '80px');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.nav-menu-wrapper')) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const { dispatch } = useAuthContext();
  const handleLoginClick = () => {
    dispatch({ type: 'OPEN_MODAL' });
  };

  const router = useRouter();
  const pathname = usePathname();

  const auth = getAuth();
  const user = auth.currentUser;
  const [startTour, setStartTour] = useState(false);
  const [isAvatarMenuObserved, setIsAvatarMenuObserved] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const conditions = [
    {
      index: 0,
      handler: () => {
        setProfileMenuOpen(true);
      },
      action: ACTIONS.UPDATE,
      lifecycle: LIFECYCLE.TOOLTIP,
    },
    {
      index: 1,
      handler: () => {
        router.push('/profile');
      },
    },
  ];

  useEffect(() => {
    if (!isAvatarMenuObserved || !user?.uid) return;
    const checkAndStartTour = async () => {
      const userGuideData = await userGuideService.getUserGuide(user.uid);
      if (userGuideData && userGuideData.docReviewTourCompletedAt) {
        return;
      }
      setStartTour(true);
    };
    void checkAndStartTour();
  }, [isAvatarMenuObserved, user?.uid]);

  const handleMenuItemClick = (menuItem: MenuItem) => {
    setMobileMenuOpen(false);
    setOpenDropdownId(null);

    if (menuItem.url) {
      if (menuItem.target === '_blank') {
        window.open(menuItem.url, '_blank');
      } else {
        window.open(menuItem.url, '_self');
      }
    }
  };

  const handleDropdownToggle = (menuId: string, hasChildren: boolean) => {
    if (hasChildren) {
      setOpenDropdownId((prev) => (prev === menuId ? null : menuId));
    }
  };

  const handleTopbarDismiss = () => {
    setTopbarDismissed(true);
    setLocalTopbarHeight(0);

    if (typeof window !== 'undefined') {
      sessionStorage.setItem('topbarDismissed', 'true');
    }

    document.documentElement.style.setProperty('--topbar-height', '0px');
    document.documentElement.style.setProperty('--total-header-height', '80px');
  };

  const handleTopbarLinkClick = (e: React.MouseEvent) => {
    const url = cmsSettings?.topbar?.linkUrl;

    if (!url) {
      e.preventDefault();
      return;
    }

    const isInternalLink = url.startsWith('/') || url.startsWith('#');

    if (isInternalLink) {
      return;
    }

    e.preventDefault();
    const target = cmsSettings?.topbar?.linkTarget || '_blank';
    const fullUrl = url.startsWith('http') ? url : `https://${url}`;
    window.open(fullUrl, target);
  };

  const isMenuItemActive = (menuItem: MenuItem): boolean => {
    if (!pathname || !menuItem.url) return false;

    const normalizePath = (path: string): string => {
      const normalized = path.toLowerCase().replace(/\/$/, '');
      return normalized === '' ? '/' : normalized;
    };

    const normalizedPathname = normalizePath(pathname);
    const normalizedUrl = normalizePath(menuItem.url);

    if (normalizedPathname === normalizedUrl) {
      return true;
    }

    if (normalizedUrl !== '/' && normalizedPathname.startsWith(normalizedUrl + '/')) {
      return true;
    }

    return false;
  };

  const NavMenuItem: React.FC<{ menuItem: MenuItem }> = ({ menuItem }) => {
    const spanRef = React.useRef<HTMLSpanElement>(null);
    const isActive = isMenuItemActive(menuItem);

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      const elementWidth = e.currentTarget.offsetWidth;
      const elementHeight = e.currentTarget.offsetHeight;
      const offsetX = e.nativeEvent.offsetX - 0.5 * elementWidth;
      const offsetY = e.nativeEvent.offsetY - 0.25 * elementHeight;
      const translateX = `calc(-50% + ${(50 * offsetX) / elementWidth}%)`;
      const translateY = `calc(-50% + ${(250 * offsetY) / elementHeight}%)`;

      if (spanRef.current) {
        spanRef.current.style.transform = `translate(${translateX}, ${translateY})`;
      }
    };

    const handleMouseLeave = () => {
      if (spanRef.current) {
        spanRef.current.style.transform = '';
      }
    };

    return (
      <NavMenuItemWrapper isActive={isActive} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
        <button
          style={{ color: menuItem?.color || '' }}
          onClick={() => handleMenuItemClick(menuItem)}
          aria-label={menuItem.title}
        >
          {menuItem.title}
          <span className="underline-effect" ref={spanRef}>
            <span />
          </span>
        </button>
      </NavMenuItemWrapper>
    );
  };

  const renderMenuItem = (menuItem: MenuItem) => {
    if (!menuItem) return null;

    const hasChildren = menuItem.children && menuItem.children.length > 0;
    const isOpen = openDropdownId === menuItem.id;

    const isChildActive = hasChildren && menuItem.children?.some((child) => isMenuItemActive(child));
    const isActive = isMenuItemActive(menuItem) || isChildActive;

    if (hasChildren) {
      return (
        <NavMenuWrapper key={menuItem.id} className="nav-menu-wrapper">
          <NavMenuTrigger
            hasChildren={true}
            isActive={isActive}
            style={{ color: menuItem?.color || '' }}
            isOpen={isOpen}
            onClick={(e) => {
              e.stopPropagation();
              handleDropdownToggle(menuItem.id, true);
            }}
            aria-expanded={isOpen}
            aria-haspopup="true"
            aria-label={menuItem.title}
          >
            {menuItem.title}
          </NavMenuTrigger>
          <DropdownMenu isOpen={isOpen}>
            {menuItem.children!.map((child) => {
              const isChildItemActive = isMenuItemActive(child);
              return (
                <DropdownItem key={child.id}>
                  <button
                    style={{
                      color: child?.color || '',
                      fontWeight: isChildItemActive ? '600' : '500',
                      backgroundColor: isChildItemActive ? '#f0f7ff' : 'transparent',
                    }}
                    onClick={() => handleMenuItemClick(child)}
                    aria-label={child.title}
                  >
                    {child.title}
                  </button>
                </DropdownItem>
              );
            })}
          </DropdownMenu>
        </NavMenuWrapper>
      );
    }

    return <NavMenuItem key={menuItem.id} menuItem={menuItem} />;
  };

  // ✅ Determine if topbar should show (only after storage is checked)
  const shouldShowTopbar = hasCheckedStorage &&
                           cmsSettings?.topbar?.enabled && 
                           cmsSettings?.topbar?.message && 
                           !topbarDismissed && 
                           !isScrolled;

  const logoSettings = cmsSettings?.logo;
  const hasCustomLogo = logoSettings?.imageUrl;

  const buttonBgColor = cmsSettings?.topbar?.buttonBackgroundColor || 'transparent';
  const buttonTextColor = cmsSettings?.topbar?.buttonTextColor || cmsSettings?.topbar?.textColor || '#ffffff';

  return (
    <>
      {/* ✅ Only render topbar AFTER we've checked sessionStorage */}
      {hasCheckedStorage && cmsSettings?.topbar?.enabled && cmsSettings?.topbar?.message && !topbarDismissed && (
        <TopbarWrapper
          ref={topbarRef}
          isVisible={!isScrolled}
          backgroundColor={cmsSettings.topbar.backgroundColor || '#1a73e8'}
          textColor={cmsSettings.topbar.textColor || '#ffffff'}
          showOnMobile={cmsSettings.topbar.showOnMobile ?? true}
          disableTransition={isInitialCheck}
        >
          <TopbarContent>
            <TopbarMessage>{cmsSettings.topbar.message}</TopbarMessage>
            {cmsSettings.topbar.linkText &&
              (() => {
                const isInternalLink =
                  cmsSettings.topbar.linkUrl?.startsWith('/') || cmsSettings.topbar.linkUrl?.startsWith('#');
                const linkTarget = isInternalLink ? '_self' : cmsSettings.topbar.linkTarget || '_blank';

                return (
                  <TopbarButton
                    buttonTextColor={buttonTextColor}
                    buttonBackgroundColor={buttonBgColor}
                    onClick={handleTopbarLinkClick}
                    href={cmsSettings.topbar.linkUrl || '#'}
                    target={linkTarget}
                    rel={linkTarget === '_blank' ? 'noopener noreferrer' : undefined}
                  >
                    {cmsSettings.topbar.linkText}
                  </TopbarButton>
                );
              })()}

            <TopbarRightSection>
              {cmsSettings.topbar.dismissible && (
                <TopbarCloseButton
                  textColor={cmsSettings.topbar.textColor || '#ffffff'}
                  onClick={handleTopbarDismiss}
                  aria-label="Dismiss announcement"
                >
                  <FAIcon name="xmark" size={14} />
                </TopbarCloseButton>
              )}
            </TopbarRightSection>
          </TopbarContent>
        </TopbarWrapper>
      )}

      <Root 
        scheme={scheme} 
        mobileMenuOpen={mobileMenuOpen} 
        topbarHeight={shouldShowTopbar ? localTopbarHeight : 0}
        disableTransition={isInitialCheck}
      >
        <Container size="xl">
          <Link href="/" id="site-logo" aria-label="Home">
            {hasCustomLogo ? (
              <Image src={logoSettings.imageUrl!} alt={logoSettings.altText || 'Site Logo'} h={50} w="auto" fit="contain" />
            ) : (
              <LogoImage />
            )}
            <span className="visually-hidden">Home</span>
          </Link>

          <button id="mobile-menu-toggle" onClick={toggleMobileMenu}>
            <span>Toggle Menu</span>
          </button>

          <nav id="site-nav">{nestedMenuList.map((menuItem) => renderMenuItem(menuItem))}</nav>

          {mounted && (
            <UserGuide
              startTour={startTour}
              setStartTour={setStartTour}
              steps={homePageProfileNavigationSteps}
              conditions={conditions}
              targetIdToObserve="avatar-menu-target"
              setIsTargetObserved={setIsAvatarMenuObserved}
            />
          )}

          <Group id="site-secondary-nav" ml="auto">
            {currentUser ? (
              <ProfileContextProvider>
                <LoginButtonWrapper
                  profileMenuOpen={profileMenuOpen}
                  setProfileMenuOpen={setProfileMenuOpen}
                  isTourActive={startTour}
                />
              </ProfileContextProvider>
            ) : (
              <Group>
                <Button href="/contact" variant="filled" color="primary">
                  Contact Sales
                </Button>

                <Button component="button" transition={false} variant="outline" color="inherit" onClick={handleLoginClick}>
                  Login
                </Button>
              </Group>
            )}
          </Group>
        </Container>
      </Root>
    </>
  );
};

export default SiteHeaderClient;